import { useRef, useState, useMemo, useEffect } from 'react'
import { useTween } from 'react-use'
import { extent, max } from 'd3-array'
import copyBlocks from './copyBlocks'

const copyBlockYearExtent = extent(copyBlocks, d => d.year)

export default function useGroveTimelineAnimation({ groveData, playing = true }) {
  const grovesReady = groveData && groveData.groves

  const yearExtent = useMemo(() => {
    if (!grovesReady) return copyBlockYearExtent
    const maxGroveYear = max(groveData.groves, g => g.yearDedicated)
    return [copyBlockYearExtent[0], Math.max(copyBlockYearExtent[1], maxGroveYear)]
  }, [grovesReady, groveData])

  const [replayCount, setReplayCount] = useState(0)
  const [highlightYearIndex, setHighlightYearIndex] = useState(-1)

  const tweenStartYear = highlightYearIndex === -1 ? copyBlocks[0].year : copyBlocks[highlightYearIndex].year
  const tweenEndYear = highlightYearIndex < copyBlocks.length - 1 ? copyBlocks[highlightYearIndex + 1].year : yearExtent[1]

  const tweenedIndex = useRef(0)
  const segmentDuration = Math.max(1500, ((tweenEndYear - tweenStartYear) / (copyBlockYearExtent[1] - copyBlockYearExtent[0])) * 5000)
  const _tweenValue = useTween('inOutCubic', playing ? (highlightYearIndex === -1 ? 0 : segmentDuration) : 1e11, 0)
  const tweenValue = playing ? _tweenValue : 0

  const currentTweenValue = tweenedIndex.current === highlightYearIndex ? tweenValue : 0

  const yearDelta = tweenEndYear - tweenStartYear
  const year = tweenStartYear + yearDelta * currentTweenValue
  const yearRounded = Math.round(year)

  const done = highlightYearIndex === copyBlocks.length - 1

  useEffect(() => {
    if (tweenValue === 1 && playing) {
      if (tweenedIndex.current === highlightYearIndex) {
        if (highlightYearIndex < copyBlocks.length - 1) {
          setHighlightYearIndex(highlightYearIndex + 1)
        }
      } else {
        if (highlightYearIndex === -1) {
          setHighlightYearIndex(0)
        }
        tweenedIndex.current = highlightYearIndex
      }
    }
  }, [tweenValue, playing, highlightYearIndex])

  const replay = () => {
    setHighlightYearIndex(-1)
    tweenedIndex.current = 0
    setReplayCount(replayCount + 1)
  }

  const skip = () => {
    setHighlightYearIndex(copyBlocks.length - 1)
    tweenedIndex.current = copyBlocks.length - 1
  }

  return {
    year,
    yearRounded,
    yearExtent,
    highlightYearIndex,
    done,
    replayCount,
    replay,
    skip,
    copyBlocks,
  }
}
